import { title } from "process";
// Push Notification Manager for ActivityLink
import {
  FirebaseMessaging,
  NotificationReceivedEvent,
} from "@capacitor-firebase/messaging";
import { LocalNotifications } from "@capacitor/local-notifications";
import { Capacitor } from "@capacitor/core";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { analytics, db, crashlytics } from "../services/firebase";
import { body } from "framer-motion/client";
import { Haptics } from "@capacitor/haptics";
import { NativeAudio } from "@capacitor-community/native-audio"; // Optional but recommended

export interface NotificationPayload {
  title: string;
  body: string;
  data?: {
    type: "activity" | "message" | "follow" | "general";
    activityId?: string;
    chatId?: string;
    userId?: string;
    link?: string;
  };
}

export class PushNotificationManager {
  private static token: string | null = null;
  private static isInitialized = false;
  private static activeChatId: string | null = null; // Track currently viewing chat

  private static tokenSaved = false;

  /**
   * Set the currently active chat ID (call when entering chat page)
   */
  static setActiveChatId(chatId: string | null) {
    this.activeChatId = chatId;
  }

  /**
   * Get the currently active chat ID
   */
  static getActiveChatId(): string | null {
    return this.activeChatId;
  }

  /**
   * Associate the current FCM token with a user after login
   */
  static async associateTokenWithUser(userId: string) {
    if (userId && this.token) {
      await this.saveTokenToUser(userId, this.token);
    }
  }

  /**
   * Initialize push notifications - should be called once on app start
   */
  static async initialize(userId?: string): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    if (this.isInitialized) {
      return true;
    }

    try {
      // 1. Check if we already have permission
      let permStatus = await FirebaseMessaging.checkPermissions();

      if (permStatus.receive === "prompt") {
        permStatus = await FirebaseMessaging.requestPermissions();
      }

      if (permStatus.receive !== "granted") {
        console.warn("⚠️ Push notification permission not granted");
        return false;
      }

      // Set up event listeners
      await this.setupFirebaseListeners(userId);
      await this.clearAllNotifications();
      this.isInitialized = true;
      return true;
    } catch (error) {
      crashlytics.logError(
        `Error in initialize: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
      return false;
    }
  }

  /**
   * Clear the app badge count (iOS only)
   */
  static async clearAllNotifications() {
    try {
      // Clear delivered notifications and badge
      await LocalNotifications.removeAllDeliveredNotifications();
      await FirebaseMessaging.removeAllDeliveredNotifications();
    } catch (error) {
      crashlytics.logError(
        `Error in clearAllNotifications: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
    }
  }

  /**
   * Set up push notification event listeners
   */
  private static async setupFirebaseListeners(userId?: string) {
    // 1. Handle Registration (Success)
    // Note: FirebaseMessaging handles registration automatically on initialization
    // but we listen for the token here.
    FirebaseMessaging.addListener("tokenReceived", (event) => {
      this.token = event.token;
      if (userId && event.token) {
        this.saveTokenToUser(userId, event.token);
      }
    });

    // 2. Notification Received (Foreground)
    FirebaseMessaging.addListener(
      "notificationReceived",
      async (event: NotificationReceivedEvent) => {

        let data: { [key: string]: any } = event.notification.data || {};

        // Check if this notification is for the currently active chat
        const chatId = data?.chatId;
        const notificationId = data?.notificationId;
        const isForActiveChat =
          chatId && this.activeChatId && chatId === this.activeChatId;
        if (isForActiveChat) {
          return; // Don't show notification for chat user is currently viewing
        }

        if (chatId) {
          try {
            // 1. Trigger Vibration
            await Haptics.vibrate();
            if (navigator.vibrate) {
              navigator.vibrate(50);
            }

            // 2. Play a Synthetic "System" Beep
            const audioCtx = new (
              window.AudioContext || (window as any).webkitAudioContext
            )();
            const oscillator = audioCtx.createOscillator();
            const gainNode = audioCtx.createGain();

            oscillator.type = "sine"; // Pure tone
            oscillator.frequency.setValueAtTime(880, audioCtx.currentTime); // High pitch A

            gainNode.gain.setValueAtTime(0.1, audioCtx.currentTime); // Volume
            gainNode.gain.exponentialRampToValueAtTime(
              0.01,
              audioCtx.currentTime + 0.2,
            );

            oscillator.connect(gainNode);
            gainNode.connect(audioCtx.destination);

            oscillator.start();
            oscillator.stop(audioCtx.currentTime + 0.2); // Short beep
          } catch (error) {
            crashlytics.logError(
              `Error in notificationReceived handler: ${JSON.stringify(error?.message)}`,
              "pushNotificationManager.ts",
              error,
            );
          }
          return;
        }
        this.showLocalNotificationFromPush(event);
      },
    );

    // 3. Notification Tapped (Action)
    FirebaseMessaging.addListener("notificationActionPerformed", (event) => {
      const data = event.notification.data;
      this.clearAllNotifications();
      this.handleNotificationAction(data);
    });

    // Register for push notifications
    // --- 4. INITIALIZATION & PERMISSIONS ---
    try {
      // Register with FCM and get the initial token
      const result = await FirebaseMessaging.getToken();
      this.token = result.token;

      if (userId) {
        this.saveTokenToUser(userId, result.token);
      }
    } catch (err) {
      // Handle registration errors here (no 'error' listener exists)
      crashlytics.logError(
        `Error in initialize: ${JSON.stringify(err?.message)}`,
        "pushNotificationManager.ts",
        err,
      );
    }
  }

  /**
   * Deregistration / Logout
   */
  static async deregister() {
    try {
      await FirebaseMessaging.deleteToken();
      this.token = null;
      this.removeToken();
    } catch (e) {
      crashlytics.logError(
        `Error in deregister: ${JSON.stringify(e?.message)}`,
        "pushNotificationManager.ts",
        e,
      );
    }
  }

  /**
   * Save FCM token to user's profile in Firebase
   */
  private static async saveTokenToUser(userId: string, token: string) {
    try {
      if (this.tokenSaved) {
        return;
      }
      const userRef = doc(db, "users", userId);
      // Use arrayUnion to add the new token to the fcmTokens array without duplicates
      await updateDoc(userRef, {
        fcmTokens: arrayUnion(token), // <--- CRUCIAL CHANGE
        fcmTokenUpdatedAt: new Date().toISOString(),
      });
      analytics.setUserProperty(
        "fcmTokenStartWith",
        token ? token.substring(0, 10) : "none",
      );
      analytics.logEvent("tokenRegistered", {
        timestamp: new Date().toISOString(),
        fcmTokenStartWith: token ? token.substring(0, 10) : "none",
        platform: Capacitor.isNativePlatform()
          ? Capacitor.getPlatform()
          : "not_native",
      });
      crashlytics.log(
        `FCM Token registered for user: ${userId}, token starts with: ${token ? token.substring(0, 10) : "none"}`,
      );
      this.tokenSaved = true;
    } catch (error) {
      crashlytics.logError(
        `Error in saveTokenToUser: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
    }
  }

  /**
   * Show local notification when push is received in foreground
   */
  private static async showLocalNotificationFromPush(
    event: NotificationReceivedEvent,
  ) {
    try {
      // Firebase puts content in event.notification and extra data in event.notification.data
      const notification = event.notification;

      // check for data.chatId for consistent notification ID
      let data: { [key: string]: any } = notification.data || {};

      const id = notification?.id ? notification?.id : Date.now().toString();
      const title = notification.title || data?.title || "Who's in?";
      const body =
        notification.body || data?.body || "You have a new notification";

      const chatId = data?.chatId;
      const notificationId = data?.notificationId;


      await LocalNotifications.schedule({
        notifications: [
          {
            title: title,
            body: body,
            id: this.hashStringToNumber(id),
            sound: "beep.wav",
            actionTypeId: "push_foreground",
            extra: {
              ...(typeof data === "object" && data !== null ? data : {}),
              originalPush: true,
            },
          },
        ],
      });
    } catch (error) {
      crashlytics.logError(
        `Error in showLocalNotificationFromPush: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
    }
  }

  /**
   * Convert string to consistent number for notification IDs
   */
  private static hashStringToNumber(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  /**
   * Handle notification tap actions
   */
  private static handleNotificationAction(data: any) {
    if (!data) return;

    const { type, activityId, chatId, userId, link } = data;

    // Use a more reliable navigation method for React Router
    const navigateToPath = (path: string) => {
      // Store navigation intent in sessionStorage for app to pick up after auth
      try {
        sessionStorage.setItem(
          "pendingNotificationNavigation",
          JSON.stringify({
            path,
            timestamp: Date.now(),
            source: "push-notification",
            notificationData: data,
          }),
        );
      } catch (e) {
        console.warn("Failed to store navigation intent:", e);
      }

      // Add a longer delay to ensure Firebase Auth has time to restore session (critical for iOS)
      setTimeout(() => {
        try {
          // Method 1: Trigger React Router navigation by updating URL and dispatching event
          window.history.pushState({}, "", path);

          // Dispatch both popstate and a custom navigation event
          window.dispatchEvent(new PopStateEvent("popstate"));

          // Also dispatch a custom event that the app can listen to
          window.dispatchEvent(
            new CustomEvent("notification-navigate", {
              detail: { path, source: "push-notification" },
            }),
          );
        } catch (error) {
          console.warn(
            "⚠️ React Router navigation failed, using location fallback:",
            error,
          );
          // Method 2: Fallback to location change with hash to force navigation
          window.location.href = path + "#from-notification";
        }
      }, 1500); // Increased delay to allow auth restoration on iOS
    };

    // Navigate based on notification type
    switch (type) {
      case "group_message":
        if (chatId) {
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=group`,
          );
        } else if (activityId) {
          navigateToPath(`/activity/${activityId}?section=chat`);
        }
        break;

      case "direct_message":
        if (userId) {
          // For direct message notifications, find the latest direct chat with the sender
          // navigateToDirectChat(userId);
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
          );
        } else if (chatId) {
          // Fallback to specific chat ID
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
          );
        }
        break;

      case "activity_member":
      case "activity_leave":
      case "activity_full":
        if (activityId) {
          navigateToPath(`/activity/${activityId}?section=participants`);
        }
        break;

      case "activity_update":
        if (activityId) {
          navigateToPath(`/activity/${activityId}?section=details`);
        }
        break;

      case "activity_cancelled":
      case "activity_kick":
        navigateToPath("/profile?tab=declined");
        break;

      case "follow_request":
        navigateToPath("/notifications?type=follow_requests");
        break;

      case "follow_accept":
      case "mutual_match":
        if (userId) {
          navigateToPath(`/profile/${userId}`);
        }
        break;

      case "story":
        if (userId) {
          navigateToPath(`/profile/${userId}?section=stories`);
        }
        break;

      case "direct_chat_start":
        if (chatId) {
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
          );
        } else if (userId) {
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=direct`,
          );
          // navigateToDirectChat(userId);
        }
        break;

      case "activity_reminder":
        if (activityId) {
          navigateToPath(`/activity/${activityId}`);
        }
        break;

      case "follower_removed":
        navigateToPath("/notifications");
        break;

      case "account_deletion":
        navigateToPath("/notifications");
        break;

      // Legacy support for existing notification types
      case "activity":
        if (activityId) {
          navigateToPath(`/activity/${activityId}`);
        }
        break;

      case "message":
        // Determine if it's group or direct based on data context
        const isGroupChat = data.isGroupChat || data.type === "group_message";
        if (isGroupChat && chatId) {
          navigateToPath(
            `/chat/${chatId}?from=notification&scroll=latest&type=group`,
          );
        } else if (userId) {
          navigateToPath(`/chat/${chatId}?from=notification&scroll=latest`);
          // For message notifications, find the latest direct chat with the sender
          // navigateToDirectChat(userId);
        } else if (chatId) {
          // Fallback to specific chat ID
          navigateToPath(`/chat/${chatId}?from=notification&scroll=latest`);
        } else if (link) {
          navigateToPath(link);
        }
        break;

      case "follow":
        if (userId) {
          navigateToPath(`/profile/${userId}`);
        } else {
          navigateToPath("/notifications");
        }
        break;

      default:
        if (link) {
          navigateToPath(link);
        } else {
          navigateToPath("/notifications");
        }
        break;
    }
  }

  /**
   * Get the current FCM token
   */
  static getToken(): string | null {
    return this.token;
  }

  /**
   * Check if push notifications are available and permitted
   */
  static async getStatus() {
    if (!Capacitor.isNativePlatform()) {
      return {
        platform: "web",
        available: false,
        permissions: "N/A",
        token: null,
      };
    }

    try {
      const permissions = await FirebaseMessaging.checkPermissions();
      return {
        platform: "native",
        available: true,
        permissions: permissions.receive,
        token: this.token,
        initialized: this.isInitialized,
      };
    } catch (error) {
      crashlytics.logError(
        `Error in getStatus: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
      return {
        platform: "native",
        available: false,
        permissions: "error",
        token: null,
      };
    }
  }

  /**
   * Request permissions if not already granted
   */
  static async requestPermissions(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const permResult = await FirebaseMessaging.requestPermissions();
      return permResult.receive === "granted";
    } catch (error) {
      crashlytics.logError(
        `Error in requestPermissions: ${JSON.stringify(error?.message)}`,
        "pushNotificationManager.ts",
        error,
      );
      return false;
    }
  }

  /**
   * Remove push notification token (for logout)
   */
  static async removeToken(userId?: string) {
    if (userId && this.token) {
      try {
        const userRef = doc(db, "users", userId);
        await updateDoc(userRef, {
          fcmTokens: arrayRemove(this.token),
          fcmTokenUpdatedAt: new Date().toISOString(),
        });
      } catch (error) {
        crashlytics.logError(
          `Error in removeToken: ${JSON.stringify(error?.message)}`,
          "pushNotificationManager.ts",
          error,
        );
      }
    }

    this.token = null;
    this.isInitialized = false;
  }
}
